import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConnectionDto, FinishRunResponse } from '@rw/shared';
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button, Card, Spinner } from '../../components/ui';
import { invalidateCompetitive, qk } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { useNetwork } from '../../stores/network';
import { toast } from '../../stores/toasts';
import { useConnections, useStravaConnect } from './strava-hooks';
import { t as tr } from '../../i18n';

const META: Record<ConnectionDto['provider'], { name: string; color: string; letter: string }> = {
  STRAVA: { name: 'Strava', color: '#FC4C02', letter: 'S' },
  GARMIN: { name: 'Garmin Connect', color: '#007CC3', letter: 'G' },
  SUUNTO: { name: 'Suunto', color: '#1a1a1a', letter: 'S' },
};

const STRAVA_RESULT: Record<string, { kind: 'success' | 'error'; title: string }> = {
  connected: { kind: 'success', title: 'Strava connected' },
  denied: { kind: 'error', title: 'Strava access was not granted' },
  missing_scope: { kind: 'error', title: 'Allow “View data about your activities” to import runs' },
  error: { kind: 'error', title: 'Could not connect Strava' },
};

export function ConnectionsCard() {
  const q = useConnections();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const online = useNetwork((s) => s.online);
  const [params, setParams] = useSearchParams();
  const fileRef = useRef<HTMLInputElement>(null);

  // Result of the Strava OAuth round-trip (?strava=connected|denied|…)
  useEffect(() => {
    const r = params.get('strava');
    if (!r) return;
    const msg = STRAVA_RESULT[r] ?? STRAVA_RESULT.error!;
    toast({ ...msg, title: tr(msg.title) });
    void qc.invalidateQueries({ queryKey: ['connections'] });
    params.delete('strava');
    setParams(params, { replace: true });
  }, [params, setParams, qc]);

  const connect = useStravaConnect();
  const setAuto = useMutation({
    mutationFn: (autoImport: boolean) => api('/integrations/strava', { method: 'PATCH', body: { autoImport } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connections'] }),
    onError: () => toast({ kind: 'error', title: tr('Could not save setting') }),
  });
  const disconnect = useMutation({
    mutationFn: () => api('/integrations/strava', { method: 'DELETE' }),
    onSuccess: () => {
      toast({ kind: 'success', title: tr('Strava disconnected') });
      void qc.invalidateQueries({ queryKey: ['connections'] });
    },
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api<FinishRunResponse>('/imports/file', { form, timeoutMs: 60_000 });
    },
    onSuccess: (r) => {
      qc.setQueryData(qk.run(r.run.id), r.run);
      void qc.invalidateQueries({ queryKey: qk.runs });
      invalidateCompetitive(qc);
      toast({ kind: r.run.status === 'VALID' ? 'success' : 'info', title: `${tr('Imported')} · ${tr(r.run.status)}` });
      navigate(`/runs/${r.run.id}`, { state: { fresh: true } });
    },
    onError: (e) => {
      // The run already there (or overlapping) opens, so it can be checked or deleted.
      const blocking = e instanceof ApiError && e.status === 409 ? (e.details as { runId?: string } | undefined)?.runId : undefined;
      toast(
        { kind: 'error', title: tr('Import failed'), body: blocking ? `${e.message}. ${tr('Delete that run first to upload this file.')}` : e instanceof ApiError ? e.message : undefined },
        9000,
      );
      if (blocking) navigate(`/runs/${blocking}`);
    },
  });

  return (
    <Card className="space-y-3 p-3" >
      <div data-testid="connections" className="space-y-3">
        {q.isPending ? (
          <div className="grid h-16 place-items-center text-volt">
            <Spinner />
          </div>
        ) : (
          q.data?.map((c) => {
            const m = META[c.provider];
            if (c.provider === 'STRAVA' && c.available) {
              return (
                <div key={c.provider} className="rounded-xl bg-ink-800 p-3" data-testid="strava-row">
                  {c.connected ? (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold">Strava</div>
                          <div className="truncate text-xs text-ink-400">Connected{c.accountName ? ` as ${c.accountName}` : ''}</div>
                        </div>
                        <Link to="/imports/strava">
                          <Button size="sm" data-testid="strava-import">
                            {tr('Import')}
                          </Button>
                        </Link>
                      </div>
                      <label className="mt-3 flex items-center justify-between gap-3 text-sm">
                        <span>
                          <span className="block font-semibold">{tr('Auto-import new runs')}</span>
                          <span className="text-xs text-ink-400">{tr('Runs you upload to Strava appear here automatically.')}</span>
                        </span>
                        <button
                          role="switch"
                          aria-checked={c.autoImport}
                          aria-label={tr('Auto-import new runs')}
                          data-testid="strava-auto-import"
                          disabled={setAuto.isPending}
                          onClick={() => setAuto.mutate(!c.autoImport)}
                          className={`relative h-7 w-12 shrink-0 rounded-full transition disabled:opacity-50 ${c.autoImport ? 'bg-volt' : 'bg-ink-600'}`}
                        >
                          <span className={`absolute top-0.5 size-6 rounded-full bg-white shadow transition-all ${c.autoImport ? 'left-[22px]' : 'left-0.5'}`} />
                        </button>
                      </label>
                      <div className="mt-3 flex items-center justify-between">
                        <img src="/brand/strava/api_logo_pwrdBy_strava_horiz_white.svg" alt="Powered by Strava" className="h-3.5" />
                        <button className="text-xs text-ink-500 underline" onClick={() => disconnect.mutate()}>
                          {tr('Disconnect')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-start gap-2">
                      <div className="text-sm text-ink-300">{tr('Import runs from your watch (COROS, Garmin, Suunto, Apple Watch) through Strava.')}</div>
                      {/* Official "Connect with Strava" button (Strava brand guidelines). */}
                      <button
                        onClick={() => connect.mutate()}
                        disabled={!online || connect.isPending}
                        aria-label="Connect with Strava"
                        data-testid="strava-connect"
                        className="disabled:opacity-50"
                      >
                        <img src="/brand/strava/btn_strava_connect_with_orange.svg" alt="Connect with Strava" height={48} className="h-12 w-auto" />
                      </button>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={c.provider} className="flex items-center gap-3 rounded-xl bg-ink-800 p-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-lg font-display text-xl font-extrabold text-white" style={{ background: m.color }}>
                  {m.letter}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{m.name}</div>
                  <div className="truncate text-xs text-ink-400">{tr(c.unavailableReason ?? 'Coming soon')}</div>
                </div>
                <span className="rounded-full bg-ink-700 px-2 py-1 text-[10px] font-bold uppercase text-ink-400">{tr('Soon')}</span>
              </div>
            );
          })
        )}

        <div className="rounded-xl border border-dashed border-ink-600 p-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-ink-700 text-xl">⌚</span>
            <div className="flex-1">
              <div className="font-semibold">{tr('Upload a run file')}</div>
              <div className="text-xs text-ink-400">{tr('GPX, TCX or FIT from any watch · last 7 days')}</div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} loading={upload.isPending} disabled={!online} data-testid="upload-file">
              {tr('Upload')}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".gpx,.tcx,.fit,application/gpx+xml,application/vnd.garmin.tcx+xml,application/octet-stream"
              className="hidden"
              data-testid="upload-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
          </div>
          <details className="mt-2 text-xs text-ink-400">
            <summary className="cursor-pointer font-semibold text-ink-300">{tr('How to export from your watch')}</summary>
            <Link to="/guide#dong-ho" className="mt-1 inline-block font-semibold text-volt" data-testid="watch-guide-link">
              {tr('Step-by-step guide with pictures')} ›
            </Link>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              <li><b>COROS:</b> {tr('COROS app → activity → ⋯ → Export data → FIT/GPX.')}</li>
              <li><b>Garmin:</b> {tr('Garmin Connect (web) → activity → ⚙ → Export Original (FIT) or GPX.')}</li>
              <li><b>Suunto:</b> {tr('Suunto app → activity → ⋯ → Export GPX.')}</li>
              <li><b>Apple Watch / Apple Health:</b> {tr('use an app like HealthFit or RunGap to export the workout as FIT/GPX (Apple Health has no web API).')}</li>
              <li>{tr('Or connect Strava above — most watches sync there automatically.')}</li>
            </ul>
          </details>
        </div>
      </div>
    </Card>
  );
}
